import React, { useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Stack,
    Paper,
    Snackbar,
    Alert,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { fetchWithAuth } from "../../utils/api";
import { formatItemFragment } from "../../utils/formatItems";
import RecentTradesList from "../../components/admin/RecentTradesList";

function renderItems(items) {
    if (Array.isArray(items)) {
        return items.map((it, idx) => (
            <React.Fragment key={idx}>
                {formatItemFragment(it.name, it.quantity, it.value_min, it.value_max)}
                {idx < items.length - 1 && " + "}
            </React.Fragment>
        ));
    }
    if (typeof items === "string") return items || "none";
    return "none";
}

const AdminViewTrades = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [username, setUsername] = useState("");
    const [limit, setLimit] = useState("");
    const [trades, setTrades] = useState([]);
    const [userInfo, setUserInfo] = useState(null);
    const [error, setError] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const [loading, setLoading] = useState(false);
    const [tradeIdsInput, setTradeIdsInput] = useState("");
    const [searchedType, setSearchedType] = useState("");

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const TRADE_ID_REGEX = /^\s*\d+(\s*,\s*\d+)*\s*$/;

    const usingTradeIds = tradeIdsInput.trim().length > 0;
    const usingUsername = username.trim().length > 0;

    const isTradeIdInputValid =
        tradeIdsInput.trim() === "" || TRADE_ID_REGEX.test(tradeIdsInput);

    const canSearch =
        (usingUsername || usingTradeIds) &&
        (!usingTradeIds || isTradeIdInputValid);

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && canSearch && !loading) {
            handleSearch();
        }
    };

    const handleSearch = async () => {
        setError("");
        setTrades([]);
        setUserInfo(null);
        setLoading(true);

        try {
            let url = "";
            let data = [];

            if (usingTradeIds) {
                if (!isTradeIdInputValid) return;
                data = await fetchWithAuth(
                    `/admin/view-trades/_?trade_ids=${encodeURIComponent(tradeIdsInput)}`
                );
                setSearchedType("ids")
            } else {
                if (!username.trim()) return;

                url = `/admin/view-trades/${encodeURIComponent(username.trim())}${limit && parseInt(limit, 10) > 0 ? `?limit=${limit}` : ""
                    }`;
                data = await fetchWithAuth(url);
                setSearchedType("username")
            }

            if (data.length === 0) {
                setSnackbar({
                    open: true,
                    message: "No trades found.",
                    severity: "info",
                });
                return;
            }

            // only set userInfo for username searches
            if (!usingTradeIds) {
                setUserInfo(data[0].user);
            }

            setTrades(data);
        } catch (err) {
            const parsedError = JSON.parse(err.message);
            setError(parsedError.error);
            setSnackbar({ open: true, message: parsedError.error, severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                p: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
            }}
        >
            <Typography variant="h4" color="accent" sx={{ mb: 3 }}>
                View Trades By Username
            </Typography>

            {/* Input Row */}
            <Stack spacing={2} sx={{ width: "100%", maxWidth: 700, mb: 3 }}>
                {/* Row 1: username + limit */}
                <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                    <TextField
                        label="username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onKeyDown={handleKeyDown}
                        fullWidth
                        disabled={usingTradeIds}
                    />

                    <TextField
                        label="trades"
                        type="number"
                        value={limit}
                        onChange={(e) => setLimit(e.target.value)}
                        onKeyDown={handleKeyDown}
                        inputProps={{ min: 1 }}
                        disabled={usingTradeIds}
                        sx={{ maxWidth: isMobile ? "100%" : 140 }}
                    />
                </Stack>

                {/* OR divider */}
                <Typography variant="body2" color="text.secondary">
                    - OR -
                </Typography>

                {/* Trade IDs */}
                <TextField
                    label="trade ids (comma separated)"
                    value={tradeIdsInput}
                    onChange={(e) => setTradeIdsInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    error={!isTradeIdInputValid}
                    helperText={
                        !isTradeIdInputValid
                            ? "Only numbers, commas, and spaces allowed"
                            : "Example: 12, 34, 56"
                    }
                    fullWidth
                    disabled={usingUsername}
                />

                {/* Search button */}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    disabled={!canSearch || loading}
                    sx={{ height: 48 }}
                >
                    {loading ? "Loading..." : "Search"}
                </Button>
            </Stack>

            {/* Error Text */}
            {error && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {/* User Info */}
            {userInfo && (
                <Box
                    sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        width: "100%",
                        maxWidth: 800,
                        textAlign: "center",
                    }}
                >
                    <Typography variant="h4" color="primary">
                        {userInfo.username}
                    </Typography>
                    <Typography variant="caption">
                        user id: {userInfo.id ?? "-"}
                        {userInfo.discord_id ? ` | Discord: ${userInfo.discord_id}` : ""}
                    </Typography>
                </Box>
            )}

            {/* Trade List */}
            {trades.length > 0 && (
                usingTradeIds && searchedType === "ids" ? (
                    <RecentTradesList trades={trades} />
                ) : (
                    <Stack spacing={2} sx={{ width: "100%", maxWidth: 800 }}>
                        {trades.map((trade) => (
                            <Paper key={trade.tradeId} sx={{ p: 2, borderRadius: 2, textAlign: "left" }}>
                                <Typography variant="h6" color="secondary">
                                    <strong>{trade.tradeDate || "-"}</strong>
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Trade ID:</strong> {trade.tradeId}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Items Sent:</strong> {renderItems(trade.itemsSent)}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Items Received:</strong> {renderItems(trade.itemsReceived)}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Notes:</strong> {trade.notes || "none"}
                                </Typography>
                            </Paper>
                        ))}
                    </Stack>
                )
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminViewTrades;
